import { useEffect } from 'react';

type EstatesListScrollState = {
  scrollTop: number;
  estateId?: string;
  pathname?: string;
  search?: string;
};

/** Remembers the scroll position and the clicked row before leaving the estates list. */
export const saveEstatesListScroll = (estateId: string) => {
  if (typeof window === 'undefined') return;

  const scrollKey = `scroll:${window.location.pathname}${window.location.search}`;
  const mainElement =
    typeof document !== 'undefined' ? document.querySelector<HTMLElement>('main.flex-1') : null;

  const scrollTop = mainElement ? mainElement.scrollTop : window.scrollY;

  const state: EstatesListScrollState = {
    scrollTop,
    estateId: String(estateId),
    pathname: window.location.pathname,
    search: window.location.search,
  };

  window.sessionStorage.setItem(scrollKey, JSON.stringify(state));
};

/** Restores the saved position (or the clicked row) when the list is shown again. */
export const useEstatesScrollRestore = () => {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const scrollKey = `scroll:${window.location.pathname}${window.location.search}`;
    const raw = window.sessionStorage.getItem(scrollKey);

    if (!raw) return;

    let parsed: EstatesListScrollState | null = null;
    let target = 0;

    try {
      parsed = JSON.parse(raw) as EstatesListScrollState;
    } catch {
      const numeric = Number(raw);
      if (!Number.isFinite(numeric) || numeric < 0) {
        window.sessionStorage.removeItem(scrollKey);
        return;
      }
      target = numeric;
    }

    const estateId = parsed?.estateId;
    const storedScrollTop = parsed?.scrollTop;

    if (parsed) {
      if (parsed.pathname && parsed.pathname !== window.location.pathname) {
        window.sessionStorage.removeItem(scrollKey);
        return;
      }
      if (parsed.search && parsed.search !== window.location.search) {
        window.sessionStorage.removeItem(scrollKey);
        return;
      }

      if (!Number.isFinite(storedScrollTop) || (storedScrollTop ?? 0) < 0) {
        window.sessionStorage.removeItem(scrollKey);
        return;
      }

      target = storedScrollTop ?? 0;
    }

    if (!Number.isFinite(target) || target < 0) {
      window.sessionStorage.removeItem(scrollKey);
      return;
    }

    const mainElement = document.querySelector<HTMLElement>('main.flex-1');
    if (!mainElement) {
      window.sessionStorage.removeItem(scrollKey);
      return;
    }

    let frameId: number | null = null;
    let attempts = 0;
    const maxAttempts = 180; // about 3s at 60fps

    const step = () => {
      attempts += 1;

      if (estateId) {
        const targetEl = document.querySelector<HTMLElement>(`[data-estate-id="${estateId}"]`);

        if (targetEl) {
          targetEl.scrollIntoView({ block: 'nearest' });
          window.sessionStorage.removeItem(scrollKey);
          return;
        }
      }

      const maxScrollTop = Math.max(mainElement.scrollHeight - mainElement.clientHeight, 0);

      if (maxScrollTop > 0) {
        const finalTop = Math.min(target, maxScrollTop);
        mainElement.scrollTop = finalTop;
      }

      if (attempts < maxAttempts) {
        frameId = window.requestAnimationFrame(step);
      } else {
        window.sessionStorage.removeItem(scrollKey);
      }
    };

    frameId = window.requestAnimationFrame(step);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);
};
